import { DetailedHTMLProps, HTMLAttributes } from "react";

import classNames from "classnames/bind";
import Carousel from "common/blocks/Carousel";
import ResponseVehicleAdCard from "common/blocks/VehicleAdCard/ResponseVehicleAdCard";
import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";

import styles from "./RecentlyViewed.module.scss";
import useGetVehicles from "./hooks/useRecentlyViewed";

const cx = classNames.bind(styles);

export type RecentlyViewedProps = {
    showBorderLine?: boolean;
    slidesOffsetBefore?: number;
    slidesOffsetAfter?: number;
    currentPdpVehicleStockNumber?: number;
    spaceBetween?: number;
    navigationClassName?: string;
    stockBookNumbers: number[];
    vehicleCardSettings: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
    components: {
        title: string;
        visibility: string;
    };
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const RecentlyViewedOff: React.FC<RecentlyViewedProps> = () => null;

const RecentlyViewedOn: React.FC<RecentlyViewedProps> = ({
    currentPdpVehicleStockNumber,
    showBorderLine = true,
    stockBookNumbers,
    vehicleCardSettings,
    vehicleDisplaySettings,
    components: { title },
}) => {
    const { isMobile, isTablet, isTabletLarge, isPortrait } = useMediaQuery();

    const { useShortlist } = useFavouritesContext();
    const shortlist = useShortlist();

    const { vehicles: recentlyViewedVehicles } = useGetVehicles(stockBookNumbers);

    let vehicles =
        recentlyViewedVehicles?.filter(
            (vehicle) => !!vehicle?.IsComingSoon || (!!vehicle?.IsOnSale && !vehicle?.IsSold)
        ) ?? [];

    if (currentPdpVehicleStockNumber)
        vehicles = vehicles.filter((vehicle) => {
            return vehicle.StockBookNumber !== currentPdpVehicleStockNumber;
        });

    if (!vehicles?.length) return null;

    const slidesOffsetBefore = isMobile ? 12 : isTablet ? 42 : isTabletLarge && isPortrait ? 22 : 0;
    const slidesOffsetAfter = isMobile ? 12 : isTablet ? 42 : isTabletLarge && isPortrait ? 22 : 0;
    const spaceBetween = isMobile ? 10 : 20;

    return (
        <div className={cx("recentlyViewed", { showBorderLine })}>
            {showBorderLine && <div className={cx("borderLine", { showBorderLine })} />}
            <Carousel
                id="VehicleCarousel"
                title={title}
                className={cx("carousel")}
                navigationClassName={cx("carouselNavigation", {
                    disabled:
                        (isMobile && vehicles?.length === 1) ||
                        (isTablet && vehicles && vehicles?.length <= 2),
                })}
                slidesOffsetBefore={slidesOffsetBefore}
                slidesOffsetAfter={slidesOffsetAfter}
                spaceBetween={spaceBetween}
            >
                {vehicles?.map((vehicle) => (
                    <ResponseVehicleAdCard
                        className={cx("vehicleAdCard")}
                        key={vehicle?.StockBookNumber}
                        {...vehicle}
                        isSaved={(shortlist?.stockNumbers ?? []).includes(vehicle?.StockBookNumber)}
                        isRecentlyViewed={true}
                        vehicleCardSettings={vehicleCardSettings}
                        vehicleDisplaySettings={vehicleDisplaySettings}
                    />
                ))}
            </Carousel>
        </div>
    );
};

const RecentlyViewed: React.FC<RecentlyViewedProps> = (props) => {
    const { components } = props;

    if (components?.visibility === "off") return <RecentlyViewedOff {...props} />;

    return <RecentlyViewedOn {...props} />;
};

export default RecentlyViewed;
